import { ArcElement, Chart, DoughnutController } from 'chart.js';
import c from 'classnames';
import React, { useEffect, useRef } from 'react';

import { Typography } from '@repo/ui';
import { formatNumberToDisplayPercentage } from 'helpers';
import { useMicrobiomeBreakdownData } from '../hooks/useMicrobiomeBreakdownData';

import { KitType } from '@repo/types';
import { AgeGroup, getAgeGroup } from '@repo/utils';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { SLIDE_BACKGROUND_COLORS } from '../slideBackgroundColors';
import styles from './MicrobiomeBreakdown.module.css';
import slideStyles from './Slides.module.css';

// Register Chart.js components
Chart.register(ArcElement, DoughnutController);

const colorMap: { [key: string]: string } = {
  green: 'hsla(172, 100%, 30%, 1)', // --green-300
  red: 'hsla(0, 63%, 61%, 1)', // --red-300
  orange: 'hsla(38, 84%, 56%, 1)', // --orange-300
  grey: 'hsla(72, 3%, 51%, 1)', // --warm-grey-600
};

type DonutChartProps = {
  categories: Array<{ name: string; color: string; value: number }>;
  centerText: string | null;
  titleText: string | null;
};

const DonutChart: React.FC<DonutChartProps> = ({
  categories,
  centerText,
  titleText,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart<'doughnut'> | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    // Create new chart
    chartRef.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: categories.map(cat => cat.name),
        datasets: [
          {
            data: categories.map(cat => cat.value),
            backgroundColor: categories.map(cat => colorMap[cat.color]),
            borderWidth: 3,
            borderColor: '#ffffff',
            borderRadius: 28,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutout: '83%',
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        animation: {
          animateRotate: true,
          duration: 1000,
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [categories]);

  return (
    <div className={styles.chartContainer}>
      <div className={styles.chartWrapper}>
        <canvas ref={canvasRef} />
        <div className={styles.chartCenterText}>
          <Typography variant='body-s'>{titleText}</Typography>
          <br />
          <Typography variant='body-m' weight='medium'>
            {centerText}
          </Typography>
        </div>
      </div>
    </div>
  );
};

function getTitle(isAdult: boolean, isVaginal: boolean, name: string) {
  if (isVaginal) {
    return 'Your vaginal community state type:';
  }

  return isAdult ? 'Your gut type:' : `${name}'s gut type:`;
}

export const MicrobiomeBreakdown: React.FC = () => {
  const { gutType, microbeCount, bacteriaCategories } =
    useMicrobiomeBreakdownData();

  const { currentKit, currentTinyAccount } = useCurrentKitAndTinyAccount();
  const isAdult =
    getAgeGroup(currentKit?.age_at_sampling ?? 0) === AgeGroup.ADULT;
  const isVaginal = currentKit?.sampling_type === KitType.VAGINAL;

  const titleText = getTitle(
    isAdult,
    isVaginal,
    currentTinyAccount?.first_name ?? '',
  );

  return (
    <div
      className={c(slideStyles.slideWrapper, styles.slideContainer)}
      style={{ backgroundColor: SLIDE_BACKGROUND_COLORS.microbiomeBreakdown }}
    >
      <div className={styles.contentContainer}>
        <Typography variant='heading-2xl' color='darkPurple' weight='medium'>
          {isAdult ? 'Your' : `${currentTinyAccount?.first_name}'s`} microbiome
          breakdown
        </Typography>

        {/* Pie chart */}
        <DonutChart
          categories={bacteriaCategories}
          centerText={gutType}
          titleText={titleText}
        />

        <Typography variant='body-m'>
          {microbeCount} types of microbes detected. This is how they breakdown:
        </Typography>

        <ul className={styles.categoryList}>
          {bacteriaCategories.map(category => (
            <li key={category.name} className={styles.categoryItem}>
              <div className={styles.categoryLabel}>
                <span className={c(styles.dot, styles[category.color])} />
                <Typography variant='body-m' weight='medium'>
                  {category.name}
                </Typography>
              </div>
              <Typography variant='body-m'>
                {formatNumberToDisplayPercentage(category.value)}
              </Typography>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
